import { useEffect, useMemo } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useTenants } from 'api/tenants';
import { MAX_ITEMS_FOR_SELECT, MAX_TENANTS_FOR_SELECT } from 'config';
import useTenantSelection from 'hooks/useTenantSelection';

interface PickerOption {
  id: number;
  label: string;
}

// The pickers sit side by side in the header, so they share one compact look — a smaller font than
// the body default and a narrower footprint — kept here so the three can never drift apart.
const PICKER_FONT_SIZE = '0.9rem';
// A floating label is shrunk to 0.75 of the field's font size by MUI, so the static label
// matches it by doing the same rather than by carrying a hand-picked size of its own.
const PICKER_LABEL_FONT_SIZE = `calc(${PICKER_FONT_SIZE} * 0.75)`;
const PICKER_MIN_WIDTH = 140;
const PICKER_MAX_WIDTH = 300;
// The box a small outlined field renders in, matched by the static-label variant so the two align.
const PICKER_HEIGHT = 40;

const pickerSx = {
  minWidth: PICKER_MIN_WIDTH,
  maxWidth: PICKER_MAX_WIDTH,
  '& .MuiInputBase-root': { fontSize: PICKER_FONT_SIZE },
  '& .MuiInputBase-input': { fontSize: PICKER_FONT_SIZE },
  '& .MuiFormLabel-root': { fontSize: PICKER_FONT_SIZE }
};

const pickerItemSx = { fontSize: PICKER_FONT_SIZE };

const pickerListboxSlotProps = { listbox: { sx: { fontSize: PICKER_FONT_SIZE } } };

interface OptionPickerProps {
  label: string;
  placeholder: string;
  options: PickerOption[];
  selectedId: number | null;
  onChange: (option: PickerOption | null) => void;
  disabled?: boolean;
  loading?: boolean;
  noOptionsText: string;
  /** Lists longer than this get a searchable autocomplete instead of a plain dropdown. */
  maxItemsForSelect?: number;
  /** Whether the picker offers a blank "All" entry — the tenant context always names one tenant. */
  allowEmpty?: boolean;
}

/**
 * One picker rendered as a static label, a plain dropdown or a searchable autocomplete, depending on
 * how many options there are. A single option is not a choice, so it reads as a label; short lists
 * are quicker to use as a dropdown; long ones are unusable without a filter. The cut-over between
 * the last two is maxItemsForSelect, from config.
 */
function OptionPicker({
  label,
  placeholder,
  options,
  selectedId,
  onChange,
  disabled = false,
  loading = false,
  noOptionsText,
  maxItemsForSelect = MAX_ITEMS_FOR_SELECT,
  allowEmpty = true
}: OptionPickerProps) {
  const selected = useMemo(() => options.find((option) => option.id === selectedId) ?? null, [options, selectedId]);

  // Only when that sole option is also what is selected. While the selection is still catching up
  // with the list — mid tenant switch, or after the user picked "All" — the control comes back, so
  // the label can never name something the rest of the app is not actually filtered to.
  if (options.length === 1 && selected) {
    return (
      // Laid out on the same box a small field occupies, with the value centred in it and the
      // caption riding the top edge where a field's shrunk label sits, so the value reads on the
      // same line as the values in the dropdowns beside it.
      <Stack sx={{ position: 'relative', height: PICKER_HEIGHT, justifyContent: 'center', minWidth: 0, maxWidth: PICKER_MAX_WIDTH }}>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ position: 'absolute', top: -7, left: 0, right: 0, fontSize: PICKER_LABEL_FONT_SIZE, lineHeight: 1.3 }}
        >
          {label}
        </Typography>
        <Typography noWrap title={selected.label} sx={{ fontSize: PICKER_FONT_SIZE, lineHeight: 1.3, minWidth: 50 }}>
          {selected.label}
        </Typography>
      </Stack>
    );
  }

  if (options.length <= maxItemsForSelect) {
    return (
      <TextField
        select
        size="small"
        label={label}
        // Falls back to blank rather than a value with no matching MenuItem, which MUI warns about
        // — happens while the list is still loading behind a restored selection.
        value={selected ? String(selected.id) : ''}
        disabled={disabled}
        onChange={(event) => {
          const { value } = event.target;
          onChange(value === '' ? null : (options.find((option) => String(option.id) === value) ?? null));
        }}
        sx={pickerSx}
      >
        {/* Kept even where empty is not a real choice while nothing is selected, so the blank value
            the field starts on still has a matching item. */}
        {(allowEmpty || !selected) && (
          <MenuItem value="" sx={pickerItemSx}>
            <em>{disabled ? noOptionsText : 'All'}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.id} value={String(option.id)} sx={pickerItemSx}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_event, value) => onChange(value)}
      disabled={disabled}
      loading={loading}
      disableClearable={!allowEmpty}
      noOptionsText={noOptionsText}
      sx={pickerSx}
      slotProps={pickerListboxSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
}

export default function TenantContextSection() {
  const { tenantId, setTenantId, selectedCompanySiteResult, setSelectedCompanySiteResult, contextAccess, contextLoading } =
    useTenantSelection();

  const { tenants: tenantList, tenantsLoaded, tenantsLoading } = useTenants(true);
  const tenantOptions = useMemo<PickerOption[]>(
    () => tenantList.map((tenant) => ({ id: tenant.tenantId, label: tenant.tenantName })),
    [tenantList]
  );

  const companies = useMemo(() => contextAccess?.companies ?? [], [contextAccess]);

  // Keep the selected tenant in sync as the (shared, cache-backed) tenant list loads or changes.
  // Gated on tenantsLoaded rather than a loading flag: an empty list only means "no tenants" once
  // the fetch has actually returned. Acting on the empty array SWR reports mid-flight would clear
  // the selection the user just made, which cascades into empty menus and access-denied redirects.
  useEffect(() => {
    if (!tenantsLoaded) return;

    if (tenantOptions.length === 0) {
      setTenantId(null);
      return;
    }

    const hasCurrent = tenantId != null && tenantOptions.some((tenant) => tenant.id === tenantId);
    if (!hasCurrent) {
      setTenantId(tenantOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantOptions, tenantsLoaded, tenantId]);

  const companyOptions = useMemo<PickerOption[]>(
    () =>
      companies.map((company) => ({ id: company.companyId, label: company.companyName })).sort((a, b) => a.label.localeCompare(b.label)),
    [companies]
  );

  const selectedCompanyId = selectedCompanySiteResult?.companyId ?? null;

  // The context lookup carries only accessible companies and their accessible site names.
  const selectedCompany = companies.find((company) => company.companyId === selectedCompanyId);
  const siteOptions = useMemo<PickerOption[]>(
    () => (selectedCompany?.sites ?? []).map((site) => ({ id: site.siteId, label: site.siteName })),
    [selectedCompany]
  );

  // The provider validates restored selections and picks an accessible default when needed.

  const handleCompanyChange = (option: PickerOption | null) => {
    // Changing company invalidates any site under the previous one.
    setSelectedCompanySiteResult(option ? { companyId: option.id, companyName: option.label, siteId: null, siteName: null } : null);
  };

  const handleSiteChange = (option: PickerOption | null) => {
    if (!selectedCompanySiteResult) return;

    setSelectedCompanySiteResult({
      ...selectedCompanySiteResult,
      siteId: option?.id ?? null,
      siteName: option?.label ?? null
    });
  };

  return (
    <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 1.5 }}>
      {/* One tenant is nothing to switch between, so the switcher is just noise. The effect above
          still pins the context to that tenant, which is what the rest of the app reads — this only
          hides the control, it does not leave the selection unset. */}
      {tenantOptions.length > 1 && (
        <OptionPicker
          label="Tenant"
          placeholder="Search tenant"
          options={tenantOptions}
          selectedId={tenantId ?? null}
          onChange={(option) => setTenantId(option?.id ?? null)}
          loading={tenantsLoading}
          noOptionsText="No tenants"
          maxItemsForSelect={MAX_TENANTS_FOR_SELECT}
          allowEmpty={false}
        />
      )}

      <OptionPicker
        label="Company"
        placeholder="Search company"
        options={companyOptions}
        selectedId={selectedCompanyId}
        onChange={handleCompanyChange}
        disabled={tenantId == null}
        loading={contextLoading}
        allowEmpty={contextAccess?.canSelectAllCompanies ?? false}
        noOptionsText={tenantId == null ? 'Select tenant first' : 'No companies'}
      />

      <OptionPicker
        label="Site"
        placeholder="Search site"
        options={siteOptions}
        selectedId={selectedCompanySiteResult?.siteId ?? null}
        onChange={handleSiteChange}
        disabled={selectedCompanyId == null}
        loading={contextLoading}
        allowEmpty={selectedCompany?.canSelectAllSites ?? false}
        noOptionsText={selectedCompanyId == null ? 'Select company first' : 'No sites'}
      />
    </Stack>
  );
}
